import { SCORE_CATEGORY_LABELS, type LeadDetailDTO, type ScoreCategory } from '@hearth/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardBody, CardHeader, useToast } from '../../../components/ui';
import { TagChip } from '../../../components/crm';
import { useLookups } from '../../../hooks/useMeta';
import { api, errorMessage } from '../../../lib/api';
import { formatDate } from '../../../lib/format';

export function ScoreCard({ lead }: { lead: LeadDetailDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const categories = lead.score.categories.filter((category) => category.components.length > 0);
  return (
    <Card>
      <CardHeader title={`Lead score ${lead.score.total}/100`} description={`Recalculated ${lead.derivedAt ? formatDate(lead.derivedAt) : 'recently'}`} />
      <CardBody className="space-y-3">
        {categories.map((category) => (
          <div key={category.category}>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{SCORE_CATEGORY_LABELS[category.category as ScoreCategory] ?? category.category}</span>
              <span className="tabular-nums text-slate-700">{category.points > 0 ? '+' : ''}{category.points}</span>
            </div>
            <ul className="mt-1 space-y-1">
              {category.components.map((component) => (
                <li key={`${component.ruleKey}:${component.signalId ?? 'rule'}`} className="flex items-start gap-2 text-[13px]">
                  <span className={component.points >= 0 ? 'tabular-nums text-emerald-700' : 'tabular-nums text-red-700'}>
                    {component.points > 0 ? '+' : ''}
                    {component.points}
                  </span>
                  <span className="min-w-0 flex-1 text-slate-700">
                    {component.label}
                    {component.detail && <span className="block text-xs text-slate-500">{component.detail}</span>}
                  </span>
                  {component.signalId && (
                    <button
                      type="button"
                      title="Remove this signal"
                      className="text-slate-400 hover:text-red-600"
                      onClick={() =>
                        void (async () => {
                          try {
                            await api.delete(`/api/signals/${component.signalId}`, { reason: 'Removed from the lead profile' });
                            await queryClient.invalidateQueries({ queryKey: ['leads', lead.id] });
                            toast.success('Signal removed');
                          } catch (error) {
                            toast.error(errorMessage(error));
                          }
                        })()
                      }
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
        {categories.length === 0 && <p className="text-[13px] text-slate-500">No scoring signals yet — this lead is still cold.</p>}
      </CardBody>
    </Card>
  );
}

export function InterestsAndTags({ lead }: { lead: LeadDetailDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { meta } = useLookups();
  const save = async (interests: string[], tagIds: string[]) => {
    try {
      await Promise.all([
        api.put(`/api/leads/${lead.id}/interests`, { categoryKeys: interests }),
        api.put(`/api/leads/${lead.id}/tags`, { tagIds }),
      ]);
      await queryClient.invalidateQueries({ queryKey: ['leads', lead.id] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };
  return (
    <Card>
      <CardHeader title="Equipment interests & tags" description="Used for matching and for tag-based scoring rules." />
      <CardBody className="space-y-3">
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Interested in</p>
          <div className="flex flex-wrap gap-1.5">
            {(meta?.productCategories ?? []).map((category) => {
              const active = lead.interests.includes(category.key);
              return (
                <button
                  key={category.key}
                  type="button"
                  onClick={() => void save(active ? lead.interests.filter((key) => key !== category.key) : [...lead.interests, category.key], lead.tagIds)}
                  className={`rounded-full border px-2 py-0.5 text-xs ${active ? 'border-ember-300 bg-ember-50 text-ember-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  {category.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Tags</p>
          <div className="flex flex-wrap gap-1.5">
            {(meta?.tags ?? []).map((tag) => {
              const active = lead.tagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => void save(lead.interests, active ? lead.tagIds.filter((id) => id !== tag.id) : [...lead.tagIds, tag.id])}
                  className={active ? 'opacity-100' : 'opacity-50 hover:opacity-80'}
                >
                  <TagChip tag={{ name: tag.name, color: tag.color }} />
                </button>
              );
            })}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export function ProposalsCard({ lead }: { lead: LeadDetailDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const resolve = async (proposalId: string, accept: boolean) => {
    try {
      if (accept) await api.post(`/api/proposals/${proposalId}/accept`);
      else await api.post(`/api/proposals/${proposalId}/reject`, { reason: 'Kept the existing value' });
      toast.success(accept ? 'Suggestion applied' : 'Suggestion dismissed');
      await queryClient.invalidateQueries({ queryKey: ['leads', lead.id] });
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };
  return (
    <Card className="border-sky-200 bg-sky-50">
      <CardHeader title="Suggested updates" description="Imported or discovered data that disagrees with what you have. Nothing is overwritten without your say-so." />
      <CardBody className="space-y-2">
        {lead.proposals.map((proposal) => (
          <div key={proposal.id} className="flex flex-wrap items-center gap-2 rounded-md border border-sky-200 bg-surface px-3 py-2 text-[13px]">
            <span className="font-medium text-slate-800">{proposal.field}</span>
            <span className="text-slate-500 line-through">{proposal.currentValue ?? 'empty'}</span>
            <span className="text-slate-400">→</span>
            <span className="text-slate-900">{proposal.proposedValue}</span>
            <Badge color="sky" size="sm">{proposal.source}</Badge>
            <div className="ml-auto flex gap-1.5">
              <Button size="sm" variant="primary" onClick={() => void resolve(proposal.id, true)}>
                Apply
              </Button>
              <Button size="sm" onClick={() => void resolve(proposal.id, false)}>
                Keep mine
              </Button>
            </div>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}
